/**
 * The dictionary, and the only thing that knows how it is stored.
 *
 * All three lengths are imported statically rather than loaded on demand. The
 * point of the game is that it works with no connection, and a dynamic import
 * is a network request in disguise: a player who first ran the game on
 * five-letter words and then, offline, flipped the toggle to six would find the
 * chunk missing and the game unable to start. One bundle, one download, every
 * setting playable afterwards.
 */

import * as len4 from './words/len4';
import * as len5 from './words/len5';
import * as len6 from './words/len6';
import type { WordLength } from './types';

const SOURCES: Record<WordLength, { ANSWERS: string; EXTRA: string }> = {
  4: len4,
  5: len5,
  6: len6,
};

/**
 * Split on demand and kept.
 *
 * Parsing all three lengths costs a few milliseconds and about a megabyte of
 * strings; doing it for the length actually being played costs a third of that
 * and happens while the player is reading the rules.
 */
const answerCache = new Map<WordLength, readonly string[]>();
const legalCache = new Map<WordLength, ReadonlySet<string>>();

/** Every word the game may choose as an answer, in dictionary order. */
export function answers(length: WordLength): readonly string[] {
  let list = answerCache.get(length);
  if (!list) {
    list = SOURCES[length].ANSWERS.split('\n');
    answerCache.set(length, list);
  }
  return list;
}

/** Is this a word the player is allowed to guess? */
export function isLegalGuess(length: WordLength, word: string): boolean {
  let set = legalCache.get(length);
  if (!set) {
    const source = SOURCES[length];
    set = new Set([...source.ANSWERS.split('\n'), ...source.EXTRA.split('\n')]);
    legalCache.set(length, set);
  }
  return set.has(word.toLowerCase());
}

/**
 * Picks `count` distinct answers.
 *
 * Distinct because two boards showing the same word is not a harder puzzle, it
 * is a bug the player would reasonably report — and with four boards in play the
 * birthday problem makes a collision likely enough to see within an evening.
 *
 * `random` is a parameter so the tests can be deterministic; nothing in the game
 * passes it.
 */
export function pickAnswers(
  length: WordLength,
  count: number,
  random: () => number = Math.random,
): string[] {
  const pool = answers(length);
  const chosen: string[] = [];
  const seen = new Set<string>();
  // The pool is three orders of magnitude larger than any `count` this is
  // called with, so rejection sampling terminates immediately in practice; the
  // attempt cap is only there so a degenerate `random` cannot hang the page.
  for (let attempt = 0; attempt < count * 100 && chosen.length < count; attempt += 1) {
    const word = pool[Math.floor(random() * pool.length) % pool.length];
    if (word === undefined || seen.has(word)) continue;
    seen.add(word);
    chosen.push(word);
  }
  // Only reachable with a pool smaller than `count`, which no shipped list is.
  while (chosen.length < count) chosen.push(pool[chosen.length % pool.length] ?? 'about');
  return chosen;
}
